/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import type { TargettedEvent } from '@suntex.core/animation';
import { Calculator, Chance, LoadUtil, UIUtil } from '@suntex.core/animation';
import { $Color } from '@suntex.core/color';
import { LogLevel, loggedContext } from '@suntex.core/logging';
import type { GameFrame } from '@suntex.modules/core';
import { ValueText } from '@suntex.modules/core';

import {
  RULES_MUL_DIV_DATA,
  RULES_MUL_DIV_EIGHTS,
  RULES_MUL_DIV_GPR,
  RULES_MUL_DIV_NINES,
  RULES_MUL_DIV_OTHERS,
  RULES_MUL_DIV_ROUNDS,
  RULES_MUL_DIV_SEVENS,
  RULES_MUL_DIV_SIXES,
  RULES_MUL_DIV_START_SECONDS,
} from './data/vifRulesMulDivData';
import {
  AUDITORY_MEMORY_SOUND_FILES_SET_ONE,
  AUDITORY_MEMORY_SOUND_FILES_SET_TWO,
} from './data/vifSounds';
import { splitStackedEquation } from './engine/boardMath';
import { cellAt } from './engine/grid';
import { RulesBoardEngine } from './engine/rulesBoardEngine';
import type { VifGameProgArgs, VifTestWindowCell } from './types';

const MUL = 3,
  DIV = 4;

const nines = RULES_MUL_DIV_NINES;
const eights = RULES_MUL_DIV_EIGHTS;
const sevens = RULES_MUL_DIV_SEVENS;
const sixes = RULES_MUL_DIV_SIXES;
const others = RULES_MUL_DIV_OTHERS;

@loggedContext({ level: LogLevel.Trace, autoLog: true })
class VIFRulesMulDivGame extends RulesBoardEngine {
  constructor(args: VifGameProgArgs) {
    super(args, 2.0);
  }

  // Working pools that persist across nextProblem calls (mutated in place).
  private pool1Used: any[] = [];
  private pool1: any[] = [];
  private pool1Answ: any[] = [];
  private pool2Used: any[] = [];
  private pool2: any[] = [];
  private pool2Answ: any[] = [];
  private poolUsedThisRound: any[][] = [[], []];
  private checkPairs: any[][] = [[]];
  private readonly fixedRounds: any[] = [];

  // Auditory-memory sound bookkeeping (persists across nextProblem/fillGridCell calls).
  private factSounds: any[] = [];
  private factAnswerSounds: any[] = [];
  private soundPool1: any[] = [];
  private soundWAnswerPool1: any[] = [];
  private tEqSounds: any[] = [];
  private tEqSoundsWAnswer: any[] = [];

  private blnGameSoundPlaying = false;
  private tintedTile: VifTestWindowCell | null = null;
  private soundQueuedRow = -1;
  private soundQueuedCol = -1;
  private blnSoundInQueue = false;
  private blnPlayingAnswer = false;
  private blnRunEnd = false;
  private blnGameStarted = false;

  private loadAuditoryMemorySoundsSetOne(gameFrame: GameFrame) {
    for (const [id, file] of Object.entries(
      AUDITORY_MEMORY_SOUND_FILES_SET_ONE,
    )) {
      gameFrame.loadSound(file, id);
    }
  }

  private loadAuditoryMemorySoundsSetTwo(gameFrame: GameFrame) {
    for (const [id, file] of Object.entries(
      AUDITORY_MEMORY_SOUND_FILES_SET_TWO,
    )) {
      gameFrame.loadSound(file, id);
    }
  }

  private loadAuditoryMemorySounds(gameFrame: GameFrame) {
    this.loadAuditoryMemorySoundsSetOne(gameFrame);
    this.loadAuditoryMemorySoundsSetTwo(gameFrame);
  }

  private enableRollovers(blnEnable: boolean) {
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const settingTile = cellAt(this.gameStage.testWindow, row, col);
        if (blnEnable) {
          UIUtil.enable(settingTile.ro!);
        } else {
          UIUtil.disable(settingTile.ro!);
        }
      }
    }
  }

  private playGameSound(
    r: number,
    c: number,
    isThisAnAnswer: boolean,
    blnQueuedSound: boolean,
  ) {
    if (!this.blnGameSoundPlaying || blnQueuedSound) {
      this.board.enabled = false;
      const soundIndex = r * 3 + c;
      this.enableRollovers(false);
      this.blnGameSoundPlaying = true;

      if (isThisAnAnswer) {
        this.tintedTile = cellAt(this.gameStage.testWindow, r, c);
        LoadUtil.playSound(this.tEqSoundsWAnswer[soundIndex]);
        ValueText.setColor(this.tintedTile.text3, $Color.from('#33EE44'));
        this.blnPlayingAnswer = true;
        if (!this.blnSoundInQueue) {
          UIUtil.setInterval(this.soundOver, 1800);
        } else {
          this.blnSoundInQueue = false;
          UIUtil.setInterval(this.soundOver, 2200);
        }
      } else {
        this.blnPlayingAnswer = false;
        this.tintedTile = cellAt(this.gameStage.testWindow, r, c);
        if (this.tintedTile.t.jiggleEnabled) {
          LoadUtil.playSound(this.tEqSounds[soundIndex]);
          ValueText.setColor(this.tintedTile.text3, $Color.from('#7AA8F9'));
          UIUtil.setInterval(this.soundOver, 1700);
        } else {
          this.blnGameSoundPlaying = false;
          this.enableRollovers(true);
          this.tintedTile = null;
          this.board.enabled = true;
        }
      }
    }
  }

  private soundOver = () => {
    UIUtil.clearInterval(this.soundOver);
    if (this.soundQueuedRow != -1) {
      if (!this.blnPlayingAnswer) {
        ValueText.setColor(this.tintedTile!.text3, $Color.from('#000000'));
      } else {
        ValueText.setColor(this.tintedTile!.text3, $Color.from('#808080'));
      }
      this.playGameSound(this.soundQueuedRow, this.soundQueuedCol, true, true);
      this.soundQueuedRow = -1;
      this.soundQueuedCol = -1;
    } else {
      this.board.enabled = true;
      this.blnGameSoundPlaying = false;
      this.enableRollovers(true);
      if (!this.blnPlayingAnswer) {
        ValueText.setColor(this.tintedTile!.text3, $Color.from('#000000'));
      } else {
        ValueText.setColor(this.tintedTile!.text3, $Color.from('#808080'));
      }
      if (this.blnRunEnd) {
        this.blnRunEnd = false;
        this.runEnd();
      }
    }
  };

  private playEquationSound = (e: object) => {
    const tile = UIUtil.getEventTarget<createjs.MovieClip>(
      e as TargettedEvent<createjs.MovieClip>,
    ).parent as VifTestWindowCell;
    const row = Number(tile.name.substr(3, 1));
    const col = Number(tile.name.substr(7, 1));
    if (!this.blnGameSoundPlaying && this.blnGameStarted && !this.dragging) {
      this.playGameSound(row, col, false, false);
    }
  };

  private runEnd() {
    this.runAnimation();

    if (this.intLayer > this.intLayersInGame || this.baseGameFlag == 5) {
      LoadUtil.playSound('win');

      if (this.intLayersInGame == 4) {
        UIUtil.setFrame(this.gmeter, 3 + this.intLayer);
      } else {
        UIUtil.setFrame(this.gmeter, 8 + this.intLayer);
      }
      this.gameOver();
    }
  }

  protected get GPR(): number[] {
    return RULES_MUL_DIV_GPR;
  }

  protected get rounds(): number[] {
    return RULES_MUL_DIV_ROUNDS;
  }

  protected computeGameFlag(gameFrame: GameFrame, moduleVariant: number) {
    let gameFlag = moduleVariant;

    if (gameFrame.moduleId == 172 && gameFlag == 0) {
      gameFlag = 0; //zero-one rule
    } else if (gameFrame.moduleId == 172 && gameFlag == 1) {
      gameFlag = 2; //mirror rule
    } else if (gameFrame.moduleId == 180 && gameFlag == 3) {
      gameFlag = 4; //tens by ones
    } else if (gameFrame.moduleId == 180 && gameFlag == 4) {
      gameFlag = 3; //double digits by single digits
    } else if (gameFrame.moduleId == 180 && gameFlag == 5) {
      gameFlag = 1; //ten by tens
    } else if (gameFrame.moduleId == 168 && gameFlag == 1) {
      gameFlag = 5; //double digits by double digits
    } else if (gameFrame.moduleId == 180 && gameFlag == 0) {
      gameFlag = 7; //Auditory Memory (Part 1)
    } else if (gameFrame.moduleId == 180 && gameFlag == 1) {
      gameFlag = 8; //Auditory Memory (Part 2)
    } else if (gameFrame.moduleId == 180 && gameFlag == 2) {
      gameFlag = 6; //Auditory Memory (ALL)
    } else if (gameFrame.moduleId == 173 && gameFlag == 0) {
      gameFlag = 10; // Divide zero-one rule
    } else if (gameFrame.moduleId == 173 && gameFlag == 1) {
      gameFlag = 12; // Divide Loose Mirror Rule
    } else if (gameFrame.moduleId == 181 && gameFlag == 0) {
      gameFlag = 16; // Divide 36 Facts Game
    } else if (gameFrame.moduleId == 181 && gameFlag == 1) {
      gameFlag = 14; // Multiply Single Digits by Tens
    } else if (gameFrame.moduleId == 181 && gameFlag == 2) {
      gameFlag = 13; // Divide Triple Digits by Single Digits
    } else if (gameFrame.moduleId == 181 && gameFlag == 3) {
      gameFlag = 11; // Multiply Double Digits by Tens
    } else if (gameFrame.moduleId == 169 && gameFlag == 1) {
      gameFlag = 15; // Dividing Multi Digits by Double Digits
    }

    return gameFlag;
  }

  protected computeBaseGameFlag(gameFlag: number) {
    return gameFlag > 9 ? gameFlag - 10 : gameFlag;
  }

  protected computeOperation(gameFlag: number) {
    return gameFlag <= 9 ? MUL : DIV;
  }

  protected initGameFlag() {
    switch (this.gameFlag) {
      case 0:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag0];
        break;
      case 1:
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag1];
        this.intLayersInGame = 4;
        this.topScaleVar = 3;
        this.scaleVar = 2;
        break;
      case 2:
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag2];
        this.intLayersInGame = 2;
        this.scaleVar = 2;
        break;
      case 3:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag3];
        this.scaleVar = 2;
        break;
      case 4:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag4];
        this.topScaleVar = 3;
        this.scaleVar = 2;
        break;
      case 5:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag5];
        this.topScaleVar = 3;
        this.scaleVar = 2;
        break;
      case 6:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag6];
        this.loadAuditoryMemorySounds(this.gameFrame);
        break;
      case 7:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag7];
        this.loadAuditoryMemorySoundsSetOne(this.gameFrame);
        break;
      case 8:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag8];
        this.loadAuditoryMemorySoundsSetTwo(this.gameFrame);
        break;
      case 10:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag10];
        break;
      case 11:
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag11];
        this.intLayersInGame = 4;
        this.topScaleVar = 3;
        this.scaleVar = 2;
        break;
      case 12:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag12];
        this.preString = 'x = ';
        this.scaleVar = 2;
        break;
      case 13:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag13];
        this.scaleVar = 2;
        this.topScaleVar = 3;
        break;
      case 14:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag14];
        this.scaleVar = 2;
        break;
      case 15:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag15];
        this.topScaleVar = 3;
        break;
      case 16:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_MUL_DIV_START_SECONDS.flag16];
        break;
      default:
        break;
    }
  }

  protected initialUiSetup() {
    if (this.intLayersInGame == 4) {
      UIUtil.setFrame(this.gmeter, 3);
    } else {
      UIUtil.setFrame(this.gmeter, 8);
    }
    if (RULES_MUL_DIV_ROUNDS[this.baseGameFlag] == 4) {
      UIUtil.setFrame(this.rmeter, 3);
    } else {
      UIUtil.setFrame(this.rmeter, 8);
    }
  }

  protected initButtons() {
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const settingTile = cellAt(this.gameStage.testWindow, row, col);
        if (this.gameFlag == 6 || this.gameFlag == 7 || this.gameFlag == 8) {
          UIUtil.tween(settingTile.ro, 'alpha', 0, 0);
          UIUtil.onClick(settingTile.ro!, this.playEquationSound);
        } else {
          UIUtil.hide(settingTile.ro, 0);
        }
      }
    }
  }

  protected onSetupGameHook() {
    UIUtil.setFrame(
      this.gameStage.rulesclip.opBtn1,
      this.gameFlag >= 9 ? DIV : MUL,
    );
  }

  protected onStopGameBegin() {
    this.stopClockTimer();
  }

  protected startGameBody() {
    if (this.baseGameFlag == 5) {
      this.resetAllTiles(true);
    } else {
      this.resetAllTiles(false);
    }

    if (this.intLayersInGame == 4) {
      UIUtil.setFrame(this.gmeter, 3);
    } else {
      UIUtil.setFrame(this.gmeter, 8);
    }
    if (RULES_MUL_DIV_ROUNDS[this.baseGameFlag] == 4) {
      UIUtil.setFrame(this.rmeter, 3);
    } else {
      UIUtil.setFrame(this.rmeter, 8);
    }

    this.gameFrame.scoreboard.hideMeter();
    this.gameFrame.scoreboard.hideMusicButton();

    this.gameFrame.scoreboard.updateRound(this.gameFrame.round);

    if (this.gameFrame.gamesPerRound == 1) {
      this.gameFrame.scoreboard.updateGame(null);
    }

    UIUtil.hide(this.gameStage.blocker);
    UIUtil.fadeTo(this.gameStage.blocker, 0, 150);
    this.blnFirstTimeLayer = [1, 1, 1, 1];
    this.blnRunEnd = false;
  }

  protected populateProblem() {
    const gameFlag = this.gameFlag;
    const intLayer = this.intLayer;
    const gameFrame = this.gameFrame;
    const blnFirstTimeLayer = this.blnFirstTimeLayer;
    const blnChosen = this.blnChosen;

    let topEQs: any[] = this.topEQs;
    let bottomAnswers: any[] = this.bottomAnswers;

    let v1 = 0;
    let v2 = 0;
    let a = 0;
    let arrZero: any;
    const zUsed = [0, 0, 0, 0, 0];
    let p;
    let tmpEQs: any[] = [];
    let tmpAns: any[] = [];
    let iMax: number | undefined;
    let iMax2: number | undefined;
    let iMin: number | undefined;
    let iMin2: number | undefined;
    let resMod10;
    let lastVal: number | undefined;
    let modNo;
    let nn;
    let alreadyExists;

    if (gameFlag == 0) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 × 1', '0 × 3', '8 × 0', '5 × 0', '7 × 0'];
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round1.layer1.topEQs];
            tmpEQs = ['2 × 1', '1 × 4', '6 × 1', '9 × 1'];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round1.layer1.bottomAnswers,
            ];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(bottomAnswers[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['2 × 0', '0 × 4', '0 × 7', '6 × 0', '0 × 5'];
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round1.layer2.bottomAnswers,
            ];
            tmpEQs = ['1 × 2', '4 × 1', '1 × 6', '1 × 9'];
            tmpAns = ['2', '4', '6', '9'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['4 × 0', '0 × 0', '3 × 0', '0 × 9', '0 × 8'];
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round1.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round1.layer3.bottomAnswers,
            ];
            tmpEQs = ['1 × 1', '9 × 1', '1 × 3', '6 × 1'];
            tmpAns = ['1', '9', '3', '6'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round1.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round1.layer4.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 × 4', '7 × 0', '0 × 0', '6 × 0', '2 × 0'];
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round2.layer1.bottomAnswers,
            ];
            tmpEQs = ['1 × 9', '4 × 1', '2 × 1', '1 × 5'];
            tmpAns = ['9', '4', '2', '5'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round2.layer2.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 × 2', '5 × 0', '0 × 6', '9 × 0', '0 × 5'];
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round2.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round2.layer3.bottomAnswers,
            ];
            tmpEQs = ['0 × 8', '1 × 9', '3 × 0', '8 × 1'];
            tmpAns = ['0', '9', '0', '8'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['8 × 0', '0 × 7', '0 × 9', '1 × 0', '4 × 0'];
            topEQs = [...RULES_MUL_DIV_DATA.flag0.round2.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag0.round2.layer4.bottomAnswers,
            ];
            tmpEQs = ['4 × 1', '1 × 5', '1 × 6', '1 × 7'];
            tmpAns = ['4', '5', '6', '7'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 1) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round1.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round1.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round1.layer2.bottomAnswers,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round1.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round1.layer3.bottomAnswers,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round1.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round1.layer4.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round2.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round2.layer2.bottomAnswers,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round2.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round2.layer3.bottomAnswers,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag1.round2.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag1.round2.layer4.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 3) {
        if (this.pool1Used.length == 0) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool1 = [...RULES_MUL_DIV_DATA.flag1.round3.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag1.round3.pool1Answers];
          this.pool2Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool2 = [...RULES_MUL_DIV_DATA.flag1.round3.pool2];
          this.pool2Answ = [...RULES_MUL_DIV_DATA.flag1.round3.pool2Answers];
        }
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [];
          bottomAnswers = [];
          for (p = 0; p < 5; p++) {
            a = Chance.randomInt(0, 40);
            while (this.pool1Used[a] == 1) {
              a = Chance.randomInt(0, 40);
            }
            this.pool1Used[a] = 1;
            topEQs.push(this.pool1[a]);
            bottomAnswers.push(this.pool1Answ[a]);
          }
          for (p = 0; p < 4; p++) {
            a = Chance.randomInt(0, 39);
            while (this.pool2Used[a] == 1) {
              a = Chance.randomInt(0, 39);
            }
            this.pool2Used[a] = 1;
            topEQs.push(this.pool2[a]);
            bottomAnswers.push(this.pool2Answ[a]);
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 4) {
        if (this.pool1Used.length == 0) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool1 = [...RULES_MUL_DIV_DATA.flag1.round4.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag1.round4.pool1Answers];
          this.pool2Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool2 = [...RULES_MUL_DIV_DATA.flag1.round4.pool2];
          this.pool2Answ = [...RULES_MUL_DIV_DATA.flag1.round4.pool2Answers];
        }

        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [];
          bottomAnswers = [];
          for (p = 0; p < 5; p++) {
            a = Chance.randomInt(0, 40);
            while (this.pool1Used[a] == 1) {
              a = Chance.randomInt(0, 40);
            }
            this.pool1Used[a] = 1;
            topEQs.push(this.pool1[a]);
            bottomAnswers.push(this.pool1Answ[a]);
          }
          for (p = 0; p < 4; p++) {
            a = Chance.randomInt(0, 39);
            while (this.pool2Used[a] == 1) {
              a = Chance.randomInt(0, 39);
            }
            this.pool2Used[a] = 1;
            topEQs.push(this.pool2[a]);
            bottomAnswers.push(this.pool2Answ[a]);
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 2) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round1.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round1.layer1.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round1.layer2.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round1.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round1.layer3.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round1.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round1.layer4.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round2.layer1.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round2.layer2.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round2.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round2.layer3.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag2.round2.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag2.round2.layer4.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 3) {
      if (gameFrame.round == 1) {
        if (this.pool1Used.length == 0) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0,
          ];
          this.pool1 = [...RULES_MUL_DIV_DATA.flag3.round1.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag3.round1.pool1Answers];
          this.pool2Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0,
          ];
          this.pool2 = [...RULES_MUL_DIV_DATA.flag3.round1.pool2];
          this.pool2Answ = [...RULES_MUL_DIV_DATA.flag3.round1.pool2Answers];
        }

        if (blnFirstTimeLayer[intLayer - 1] == 0) {
          for (p = 0; p < this.poolUsedThisRound[0]!.length; p++) {
            this.pool1Used[this.poolUsedThisRound[0]![p]!] = 0;
          }
          for (p = 0; p < this.poolUsedThisRound[1]!.length; p++) {
            this.pool2Used[this.poolUsedThisRound[1]![p]!] = 0;
          }
        }
        this.poolUsedThisRound = [[], []];
        let nTop = 0;
        nTop = 6 - intLayer;

        topEQs = [];
        bottomAnswers = [];
        modNo = 2;
        if (gameFrame.round >= 3) {
          modNo = 4;
        }
        for (p = 0; p < nTop; p++) {
          a = Chance.randomInt(0, 71);
          while (this.pool1Used[a] == 1) {
            a = Chance.randomInt(0, 71);
          }
          if (p % modNo == 0) {
            lastVal = Calculator.getInt(this.pool1Answ[a]);
          } else {
            while (
              lastVal! % 10 != Calculator.getInt(this.pool1Answ[a]) % 10 ||
              this.pool1Used[a] == 1
            ) {
              a = Chance.randomInt(0, 71);
            }
          }
          this.pool1Used[a] = 1;
          topEQs.push(this.pool1[a]);
          bottomAnswers.push(this.pool1Answ[a]);
          this.poolUsedThisRound[0]!.push(a);
        }
        for (p = 0; p < 3 + intLayer; p++) {
          a = Chance.randomInt(0, 53);
          while (this.pool2Used[a] == 1) {
            a = Chance.randomInt(0, 53);
          }
          if (p % modNo == 0 || p == 0) {
            lastVal = Calculator.getInt(this.pool2Answ[a]);
          } else {
            while (
              lastVal! % 10 != Calculator.getInt(this.pool2Answ[a]) % 10 ||
              this.pool2Used[a] == 1
            ) {
              a = Chance.randomInt(0, 53);
            }
          }
          this.pool2Used[a] = 1;
          topEQs.push(this.pool2[a]);
          bottomAnswers.push(this.pool2Answ[a]);
          this.poolUsedThisRound[1]!.push(a);
        }
      } else if (gameFrame.round >= 2) {
        topEQs = [];
        bottomAnswers = [];
        if (gameFrame.round == 2) {
          iMax = 49;
        } else if (gameFrame.round == 3) {
          iMax = 79;
        } else {
          iMax = 99;
        }
        this.poolUsedThisRound = [[], []];
        modNo = 2;
        if (gameFrame.round >= 3) {
          modNo = 4;
        }
        for (p = 0; p < 9; p++) {
          v1 = Chance.randomInt(10, iMax);
          if (intLayer < 4) {
            v2 = Chance.randomInt(2, intLayer + 4);
          } else {
            v2 = Chance.randomInt(2, 9);
          }
          if (p % modNo == 0) {
            resMod10 = (v1 * v2) % 10;
            lastVal = v1 * v2;
          } else {
            while (
              (v1 * v2) % 10 != resMod10 ||
              lastVal == v1 * v2 ||
              (lastVal! > v1 * v2 && lastVal! > v1 * v2 * 2) ||
              (v1 * v2 > lastVal! && v1 * v2 > lastVal! * 2)
            ) {
              v1 = Chance.randomInt(10, iMax);
              if (intLayer < 4) {
                v2 = Chance.randomInt(2, intLayer + 4);
              } else {
                v2 = Chance.randomInt(2, 9);
              }
            }
          }
          if (gameFrame.round >= 3 && p > 4) {
            modNo = 99;
          }
          if (
            gameFrame.round == 2 ||
            intLayer <= 2 ||
            Chance.randomInt(0, 1) == 0
          ) {
            topEQs.push(v1.toString() + ' × ' + v2.toString());
          } else {
            topEQs.push(v2.toString() + ' × ' + v1.toString());
          }
          bottomAnswers.push((v1 * v2).toString());
        }
      }
      blnFirstTimeLayer[intLayer - 1] = 0;
      blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    } else if (gameFlag == 4 || gameFlag == 14) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round1.layer1.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round1.layer1.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round1.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round1.layer2.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round1.layer2.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round1.layer2.bottomAnswers,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round1.layer3.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round1.layer3.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round1.layer3.bottomAnswers,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round1.layer4.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round1.layer4.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round1.layer4.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round2.layer1.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round2.layer1.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round2.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round2.layer2.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round2.layer2.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round2.layer2.bottomAnswers,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round2.layer3.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round2.layer3.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round2.layer3.bottomAnswers,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            if (gameFlag == 4) {
              topEQs = [...RULES_MUL_DIV_DATA.flag4.round2.layer4.topEQs];
            } else {
              topEQs = [...RULES_MUL_DIV_DATA.flag14.round2.layer4.topEQs];
            }
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag4.round2.layer4.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 3) {
        if (this.pool1Used.length == 0) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool1 = [...RULES_MUL_DIV_DATA.flag4.round3.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag4.round3.pool1Answers];
          this.pool2Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool2 = [...RULES_MUL_DIV_DATA.flag4.round3.pool2];
          this.pool2Answ = [...RULES_MUL_DIV_DATA.flag4.round3.pool2Answers];
        }
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [];
          bottomAnswers = [];
          for (p = 0; p < 5; p++) {
            a = Chance.randomInt(0, 40);
            while (this.pool1Used[a] == 1) {
              a = Chance.randomInt(0, 40);
            }
            this.pool1Used[a] = 1;
            topEQs.push(this.pool1[a]);
            bottomAnswers.push(this.pool1Answ[a]);
          }
          for (p = 0; p < 4; p++) {
            a = Chance.randomInt(0, 39);
            while (this.pool2Used[a] == 1) {
              a = Chance.randomInt(0, 39);
            }
            this.pool2Used[a] = 1;
            topEQs.push(this.pool2[a]);
            bottomAnswers.push(this.pool2Answ[a]);
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 4) {
        if (this.pool1Used.length == 0) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool1 = [...RULES_MUL_DIV_DATA.flag4.round4.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag4.round4.pool1Answers];
          this.pool2Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.pool2 = [...RULES_MUL_DIV_DATA.flag4.round4.pool2];
          this.pool2Answ = [...RULES_MUL_DIV_DATA.flag4.round4.pool2Answers];
        }
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [];
          bottomAnswers = [];
          for (p = 0; p < 5; p++) {
            a = Chance.randomInt(0, 40);
            while (this.pool1Used[a] == 1) {
              a = Chance.randomInt(0, 40);
            }
            this.pool1Used[a] = 1;
            topEQs.push(this.pool1[a]);
            bottomAnswers.push(this.pool1Answ[a]);
          }
          for (p = 0; p < 4; p++) {
            a = Chance.randomInt(0, 39);
            while (this.pool2Used[a] == 1) {
              a = Chance.randomInt(0, 39);
            }
            this.pool2Used[a] = 1;
            topEQs.push(this.pool2[a]);
            bottomAnswers.push(this.pool2Answ[a]);
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 5) {
      if (gameFrame.round >= 1) {
        topEQs = [];
        bottomAnswers = [];
        switch (gameFrame.round) {
          case 1:
            switch (intLayer) {
              case 1:
                iMax = 49;
                iMax2 = 39;
                break;
              case 2:
                iMax = 69;
                iMax2 = 59;
                break;
              case 3:
                iMax = 89;
                iMax2 = 79;
                break;
              case 4:
                iMax = 99;
                iMax2 = 99;
                break;
            }
            break;
          case 2:
            switch (intLayer) {
              case 1:
                iMax = 39;
                iMax2 = 49;
                break;
              case 2:
                iMax = 59;
                iMax2 = 69;
                break;
              case 3:
                iMax = 79;
                iMax2 = 89;
                break;
              case 4:
                iMax = 99;
                iMax2 = 99;
                break;
            }
            break;
          case 3:
            iMax = 99;
            iMax2 = 99;
            break;
          case 4:
            iMax = 99;
            iMax2 = 99;
            break;
        }
        modNo = 2;
        if (gameFrame.round >= 3) {
          modNo = 4;
        }
        for (p = 0; p < 9; p++) {
          v1 = Chance.randomInt(10, iMax!);
          v2 = Chance.randomInt(10, iMax2!);

          if (p % modNo == 0) {
            resMod10 = (v1 * v2) % 10;
            lastVal = v1 * v2;
          } else {
            while (
              (v1 * v2) % 10 != resMod10 ||
              lastVal == v1 * v2 ||
              (lastVal! > v1 * v2 && lastVal! > v1 * v2 * 2) ||
              (v1 * v2 > lastVal! && v1 * v2 > lastVal! * 2)
            ) {
              v1 = Chance.randomInt(10, iMax!);
              v2 = Chance.randomInt(10, iMax2!);
            }
          }
          if (gameFrame.round >= 3 && p > 4) {
            modNo = 99;
          }
          topEQs.push(v1.toString() + ' × ' + v2.toString());
          bottomAnswers.push((v1 * v2).toString());
        }
      }
      blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    } else if (gameFlag == 6) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round1.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer1.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer1.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer1.factAnswerSounds,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer2.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer2.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer2.factAnswerSounds,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round1.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer3.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer3.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer3.factAnswerSounds,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round1.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer4.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer4.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round1.layer4.factAnswerSounds,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer1.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer1.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer1.factAnswerSounds,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer2.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer2.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer2.factAnswerSounds,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round2.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer3.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer3.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer3.factAnswerSounds,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag6.round2.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer4.bottomAnswers,
            ];
            this.factSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer4.factSounds,
            ];
            this.factAnswerSounds = [
              ...RULES_MUL_DIV_DATA.flag6.round2.layer4.factAnswerSounds,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round >= 3) {
        if (intLayer == 1) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
        }
        if (this.pool1.length == 0) {
          this.pool1 = [...RULES_MUL_DIV_DATA.flag6.round3.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag6.round3.pool1Answers];
          this.soundPool1 = [...RULES_MUL_DIV_DATA.flag6.round3.soundPool1];
          this.soundWAnswerPool1 = [
            ...RULES_MUL_DIV_DATA.flag6.round3.soundWAnswerPool1,
          ];
        }
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [];
          bottomAnswers = [];
          this.factSounds = [];
          this.factAnswerSounds = [];
          for (p = 0; p < 9; p++) {
            a = Chance.randomInt(0, 35);
            while (this.pool1Used[a] == 1) {
              a = Chance.randomInt(0, 35);
            }
            this.pool1Used[a] = 1;
            topEQs.push(this.pool1[a]);
            bottomAnswers.push(this.pool1Answ[a]);
            this.factSounds.push(this.soundPool1[a]);
            this.factAnswerSounds.push(this.soundWAnswerPool1[a]);
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 7) {
      if (blnFirstTimeLayer[intLayer - 1] == 1) {
        topEQs = [...RULES_MUL_DIV_DATA.flag7.topEQs];
        bottomAnswers = [...RULES_MUL_DIV_DATA.flag7.bottomAnswers];
        this.factSounds = [...RULES_MUL_DIV_DATA.flag7.factSounds];
        this.factAnswerSounds = [...RULES_MUL_DIV_DATA.flag7.factAnswerSounds];
      }
      blnFirstTimeLayer[intLayer - 1] = 0;
      blnChosen[intLayer - 1] = [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      ];
    } else if (gameFlag == 8) {
      if (blnFirstTimeLayer[intLayer - 1] == 1) {
        topEQs = [...RULES_MUL_DIV_DATA.flag8.topEQs];
        bottomAnswers = [...RULES_MUL_DIV_DATA.flag8.bottomAnswers];
        this.factSounds = [...RULES_MUL_DIV_DATA.flag8.factSounds];
        this.factAnswerSounds = [...RULES_MUL_DIV_DATA.flag8.factAnswerSounds];
      }
      blnFirstTimeLayer[intLayer - 1] = 0;
      blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    } else if (gameFlag == 10) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 ÷ 1', '0 ÷ 3', '0 ÷ 8', '0 ÷ 5', '0 ÷ 7'];
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round1.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round1.layer1.bottomAnswers,
            ];
            tmpEQs = ['2 ÷ 2', '4 ÷ 4', '6 ÷ 6', '9 ÷ 9'];
            tmpAns = ['1', '1', '1', '1'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 ÷ 2', '0 ÷ 4', '0 ÷ 7', '0 ÷ 6', '0 ÷ 5'];
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round1.layer2.bottomAnswers,
            ];
            tmpEQs = ['2 ÷ 2', '4 ÷ 4', '6 ÷ 6', '9 ÷ 9'];
            tmpAns = ['1', '1', '1', '1'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 ÷ 4', '0 ÷ 2', '0 ÷ 3', '0 ÷ 9', '0 ÷ 8'];
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round1.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round1.layer3.bottomAnswers,
            ];
            tmpEQs = ['1 ÷ 1', '9 ÷ 9', '3 ÷ 3', '6 ÷ 6'];
            tmpAns = ['1', '1', '1', '1'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round1.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round1.layer4.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 ÷ 4', '0 ÷ 7', '0 ÷ 5', '0 ÷ 6', '0 ÷ 2'];
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round2.layer1.bottomAnswers,
            ];
            tmpEQs = ['9 ÷ 9', '4 ÷ 4', '2 ÷ 2', '5 ÷ 5'];
            tmpAns = ['1', '1', '1', '1'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round2.layer2.bottomAnswers,
            ];
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 ÷ 2', '0 ÷ 5', '0 ÷ 6', '0 ÷ 9', '0 ÷ 7'];
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round2.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round2.layer3.bottomAnswers,
            ];
            tmpEQs = ['8 ÷ 8', '9 ÷ 9', '3 ÷ 3', '8 ÷ 8'];
            tmpAns = ['1', '1', '1', '1'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            arrZero = ['0 ÷ 8', '0 ÷ 7', '0 ÷ 9', '0 ÷ 1', '0 ÷ 4'];
            topEQs = [...RULES_MUL_DIV_DATA.flag10.round2.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag10.round2.layer4.bottomAnswers,
            ];
            tmpEQs = ['4 ÷ 4', '5 ÷ 5', '6 ÷ 6', '7 ÷ 7'];
            tmpAns = ['1', '1', '1', '1'];
            a = Chance.randomInt(0, 3);
            topEQs.push(tmpEQs[a]);
            bottomAnswers.push(tmpAns[a]);
            for (p = 0; p < 4; p++) {
              a = Chance.randomInt(0, 4);
              while (zUsed[a] == 1) {
                a = Chance.randomInt(0, 4);
              }
              zUsed[a] = 1;
              topEQs.push(arrZero[a]);
              bottomAnswers.push('0');
            }
            blnFirstTimeLayer[intLayer - 1] = 0;
          }
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 11) {
      topEQs = [];
      bottomAnswers = [];
      if (gameFrame.round <= 2) {
        if (intLayer == 1 && gameFrame.round == 1) {
          iMin = 10;
        } else if (intLayer == 2 && gameFrame.round == 1) {
          iMin = 20;
        } else if (intLayer == 3 && gameFrame.round == 1) {
          iMin = 30;
        } else if (intLayer == 4 && gameFrame.round == 1) {
          iMin = 40;
        } else if (intLayer == 1 && gameFrame.round == 2) {
          iMin = 50;
        } else if (intLayer == 2 && gameFrame.round == 2) {
          iMin = 60;
        } else if (intLayer == 3 && gameFrame.round == 2) {
          iMin = 70;
        } else if (intLayer == 4 && gameFrame.round == 2) {
          iMin = 80;
        }
        this.checkPairs = [[]];
        p = 0;
        while (p < 9) {
          const pair: any[] = [];
          v1 = Chance.randomInt(10, 99);
          if (p <= 3) {
            v2 = iMin!;
          } else {
            v2 = iMin! + 10;
          }
          nn = 0;
          alreadyExists = 0;
          while (nn < this.checkPairs.length && alreadyExists == 0) {
            if (
              this.checkPairs[nn]![0] == v1 &&
              this.checkPairs[nn]![1] == v2
            ) {
              alreadyExists = 1;
            }
            nn++;
          }
          if (alreadyExists == 0) {
            pair.push(v1);
            pair.push(v2);
            this.checkPairs.push(pair);
            topEQs.push(v1.toString() + ' × ' + v2.toString());
            bottomAnswers.push((v1 * v2).toString());
            p++;
          }
        }
      } else if (gameFrame.round >= 3) {
        this.checkPairs = [[]];
        p = 0;
        while (p < 9) {
          const pair: any[] = [];
          v1 = Chance.randomInt(10, 99);
          v2 = Chance.randomInt(1, 9);
          nn = 0;
          alreadyExists = 0;
          while (nn < this.checkPairs.length && alreadyExists == 0) {
            if (
              this.checkPairs[nn]![0] == v1 &&
              this.checkPairs[nn]![1] == v2
            ) {
              alreadyExists = 1;
            }
            nn++;
          }
          if (alreadyExists == 0) {
            pair.push(v1);
            pair.push(v2);
            this.checkPairs.push(pair);
            topEQs.push(v1.toString() + ' × ' + (v2 * 10).toString());
            bottomAnswers.push((v1 * v2 * 10).toString());
            p++;
          }
        }
      }
      blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    } else if (gameFlag == 12) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag12.round1.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag12.round1.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag12.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag12.round1.layer2.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag12.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag12.round2.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag12.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag12.round2.layer2.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 13) {
      if (gameFrame.round >= 1) {
        topEQs = [];
        bottomAnswers = [];
        switch (gameFrame.round) {
          case 1:
            iMin = 2;
            iMax = 4;
            iMin2 = 11;
            iMax2 = 39;
            break;
          case 2:
            iMin = 4;
            iMax = 6;
            iMin2 = 31;
            iMax2 = 59;
            break;
          case 3:
            iMin = 5;
            iMax = 8;
            iMin2 = 41;
            iMax2 = 79;
            break;
          case 4:
            iMin = 2;
            iMax = 9;
            iMin2 = 11;
            iMax2 = 99;
            break;
        }
        this.checkPairs = [[]];
        p = 0;
        while (p < 9) {
          const pair: any[] = [];
          v1 = Chance.randomInt(iMin!, iMax!);
          v2 = Chance.randomInt(iMin2!, iMax2!);
          nn = 0;
          alreadyExists = 0;
          while (nn < this.checkPairs.length && alreadyExists == 0) {
            if (
              this.checkPairs[nn]![0] == v1 &&
              this.checkPairs[nn]![1] == v2
            ) {
              alreadyExists = 1;
            }
            nn++;
          }
          if (alreadyExists == 0) {
            pair.push(v1);
            pair.push(v2);
            this.checkPairs.push(pair);
            topEQs.push((v1 * v2).toString() + ' ÷ ' + v1.toString());
            bottomAnswers.push(v2.toString());
            p++;
          }
        }
      }
      blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    } else if (gameFlag == 15) {
      if (gameFrame.round >= 1) {
        topEQs = [];
        bottomAnswers = [];
        switch (gameFrame.round) {
          case 1:
            iMax = 39;
            iMax2 = 29;
            break;
          case 2:
            iMax = 59;
            iMax2 = 49;
            break;
          case 3:
            iMax = 79;
            iMax2 = 69;
            break;
          case 4:
            iMax = 99;
            iMax2 = 89;
            break;
        }
        modNo = 2;
        if (gameFrame.round >= 3) {
          modNo = 4;
        }
        for (p = 0; p < 9; p++) {
          v1 = Chance.randomInt(11, iMax!);
          v2 = Chance.randomInt(11, iMax2!);

          if (p % modNo == 0) {
            resMod10 = (v1 * v2) % 10;
            lastVal = v1 * v2;
          } else {
            while (
              (v1 * v2) % 10 != resMod10 ||
              lastVal == v1 * v2 ||
              (lastVal! > v1 * v2 && lastVal! > v1 * v2 * 2) ||
              (v1 * v2 > lastVal! && v1 * v2 > lastVal! * 2)
            ) {
              v1 = Chance.randomInt(11, iMax!);
              v2 = Chance.randomInt(11, iMax2!);
            }
          }
          if (gameFrame.round >= 3 && p > 4) {
            modNo = 99;
          }
          topEQs.push((v1 * v2).toString() + ' ÷ ' + v2.toString());
          bottomAnswers.push(v1.toString());
        }
      }
      blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    } else if (gameFlag == 16) {
      if (gameFrame.round == 1) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round1.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round1.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round1.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round1.layer2.bottomAnswers,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round1.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round1.layer3.bottomAnswers,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round1.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round1.layer4.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round == 2) {
        if (intLayer == 1) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round2.layer1.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round2.layer1.bottomAnswers,
            ];
          }
        } else if (intLayer == 2) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round2.layer2.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round2.layer2.bottomAnswers,
            ];
          }
        } else if (intLayer == 3) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round2.layer3.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round2.layer3.bottomAnswers,
            ];
          }
        } else if (intLayer == 4) {
          if (blnFirstTimeLayer[intLayer - 1] == 1) {
            topEQs = [...RULES_MUL_DIV_DATA.flag16.round2.layer4.topEQs];
            bottomAnswers = [
              ...RULES_MUL_DIV_DATA.flag16.round2.layer4.bottomAnswers,
            ];
          }
        }
        blnFirstTimeLayer[intLayer - 1] = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (gameFrame.round >= 3) {
        if (intLayer == 1) {
          this.pool1Used = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
          this.fixedRounds.push(Chance.randomInt(1, 4));
          p = 0;
          while (p < 3) {
            if (this.fixedRounds[p] == 4) {
              this.fixedRounds.push(1);
            } else {
              this.fixedRounds.push(this.fixedRounds[p] + 1);
            }
            p++;
          }
        }
        if (this.pool1.length == 0) {
          this.pool1 = [...RULES_MUL_DIV_DATA.flag16.round3.pool1];
          this.pool1Answ = [...RULES_MUL_DIV_DATA.flag16.round3.pool1Answers];
        }

        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          const countAnswer = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
          let o;
          topEQs = [];
          bottomAnswers = [];
          this.factSounds = [];
          this.factAnswerSounds = [];
          for (p = 0; p < 9; p++) {
            //code to ensure that we don't get 5 or more 9's,8's,7's, or 6's in any one layer...which would cause an infinite loop
            if (p == 0 || p == 4) {
              o = nines[Chance.randomInt(0, nines.length - 1)]!;
            } else if (p == 1 || (p == 5 && intLayer != this.fixedRounds[3])) {
              o = eights[Chance.randomInt(0, eights.length - 1)]!;
            } else if (
              p == 2 ||
              (p == 6 &&
                intLayer != this.fixedRounds[1] &&
                intLayer != this.fixedRounds[2])
            ) {
              o = sevens[Chance.randomInt(0, sevens.length - 1)]!;
            } else if (p == 3 || (p == 7 && intLayer == this.fixedRounds[3])) {
              o = sixes[Chance.randomInt(0, sixes.length - 1)]!;
            } else {
              o = others[Chance.randomInt(0, others.length - 1)]!;
            }
            while (this.pool1Used[o] == 1) {
              if (p == 0 || p == 4) {
                o = nines[Chance.randomInt(0, nines.length - 1)]!;
              } else if (
                p == 1 ||
                (p == 5 && intLayer != this.fixedRounds[3])
              ) {
                o = eights[Chance.randomInt(0, eights.length - 1)]!;
              } else if (
                p == 2 ||
                (p == 6 &&
                  intLayer != this.fixedRounds[1] &&
                  intLayer != this.fixedRounds[2])
              ) {
                o = sevens[Chance.randomInt(0, sevens.length - 1)]!;
              } else if (
                p == 3 ||
                (p == 7 && intLayer == this.fixedRounds[3])
              ) {
                o = sixes[Chance.randomInt(0, sixes.length - 1)]!;
              } else {
                o = others[Chance.randomInt(0, others.length - 1)]!;
              }
            }

            countAnswer[Number(this.pool1Answ[o])]!++;
            this.pool1Used[o] = 1;
            topEQs.push(this.pool1[o]);
            bottomAnswers.push(this.pool1Answ[o]);
            this.factSounds.push(this.soundPool1[o]);
            this.factAnswerSounds.push(this.soundWAnswerPool1[o]);
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    }

    this.topEQs = topEQs;
    this.bottomAnswers = bottomAnswers;
  }

  protected fillGridCell(row: number, col: number, selectedIndex: number) {
    const settingTile = cellAt(this.board, row, col);
    if (this.gameFlag == 12) {
      const [topLine, bottomLine] = splitStackedEquation(
        this.topEQs[selectedIndex]!,
      );
      ValueText.displayTwoLines(settingTile.text3, topLine, bottomLine);
    } else {
      ValueText.displayText(
        settingTile.text3,
        this.topEQs[selectedIndex],
        this.topScaleVar,
      );
      if (this.gameFlag == 6 || this.gameFlag == 7 || this.gameFlag == 8) {
        this.tEqSounds[row * 3 + col] = this.factSounds[selectedIndex];
        this.tEqSoundsWAnswer[row * 3 + col] =
          this.factAnswerSounds[selectedIndex];
      }
    }
  }

  protected finalizeNextProblem() {
    this.board.enabled = true;
    if (this.intLayersInGame == 4) {
      UIUtil.setFrame(this.gmeter, this.intLayer + 3);
    } else {
      UIUtil.setFrame(this.gmeter, this.intLayer + 8);
    }
    if (RULES_MUL_DIV_ROUNDS[this.baseGameFlag] == 4) {
      UIUtil.setFrame(this.rmeter, this.gameFrame.round + 3);
    } else {
      UIUtil.setFrame(this.rmeter, this.gameFrame.round + 8);
    }
  }

  protected isAltFrameGameFlag(gameFlag: number) {
    return (
      gameFlag == 1 ||
      gameFlag == 2 ||
      gameFlag == 5 ||
      gameFlag == 11 ||
      gameFlag == 12
    );
  }

  protected canStartDrag() {
    return !this.blnGameSoundPlaying;
  }

  protected dragMoveBringToFrontTarget() {
    return this.draggingPiece!.parent;
  }

  protected getMouseCoordinatesHook(e: createjs.MouseEvent) {
    const point = UIUtil.getMousePosition(e);
    if (
      point.y < 73 &&
      !this.dragging &&
      (this.gameFlag == 6 || this.gameFlag == 7 || this.gameFlag == 8)
    ) {
      return { row: -1, col: -1 };
    }
    return {
      row: Math.min(2, Math.floor(point.y / 183)),
      col: Math.min(2, Math.floor(point.x / 183)),
    };
  }

  protected isPointOutOfBounds(point: { x: number; y: number }) {
    return point.x < 10 || point.x > 570 || point.y < 2 || point.y > 552;
  }

  protected computeHoverCell(point: { x: number; y: number }) {
    let tRow: number | null = null;
    let tCol: number | null = null;

    if (point.x >= 15 && point.x <= 187) {
      tCol = 0;
    } else if (point.x >= 198 && point.x <= 374) {
      tCol = 1;
    } else if (point.x >= 388 && point.x <= 561) {
      tCol = 2;
    }

    if (point.y >= -58 && point.y <= 110) {
      tRow = 0;
    } else if (point.y >= 123 && point.y <= 294) {
      tRow = 1;
    } else if (point.y >= 301 && point.y <= 475) {
      tRow = 2;
    }

    return { row: tRow, col: tCol };
  }

  protected onHoverCellChange(tRow: number | null, tCol: number | null) {
    this.fadeAllTiles(1, 75);
    if (this.currRow != null && this.currCol != null) {
      const fadingOutTile = cellAt(this.board, this.currRow, this.currCol);
      UIUtil.tween(fadingOutTile, 'alpha', 1, 0);
    }
    this.currCol = tCol;
    this.currRow = tRow;
    if (this.currRow != null && this.currCol != null) {
      if (
        (this.currCol != this.startCol || this.currRow != this.startRow) &&
        !this.tileLocked[this.currRow]![this.currCol]
      ) {
        const fadingInTile = cellAt(this.board, this.currRow, this.currCol);
        UIUtil.tween(fadingInTile, 'alpha', 0.3, 75);
      }
    }
  }

  protected canTradePieceHook() {
    return !this.blnGameSoundPlaying;
  }

  protected onCorrectMatch(tValue: string | number) {
    this.checkForBonus(tValue);
    this.correctAnswer();
  }

  protected playCorrectSoundHook() {
    if (this.gameFlag == 6 || this.gameFlag == 7 || this.gameFlag == 8) {
      if (this.intCorrect >= 8) {
        this.stopClockTimer();
      }
      this.playGameSound(this.currRow!, this.currCol!, true, false);
    } else {
      LoadUtil.playSound('correct');
    }
  }

  protected onBonusSoundQueueHook() {
    if (this.gameFlag == 6 || this.gameFlag == 7 || this.gameFlag == 8) {
      this.blnSoundInQueue = true;
      this.soundQueuedRow = this.startRow!;
      this.soundQueuedCol = this.startCol!;
    }
  }

  protected checkForEndBody() {
    if (this.intCorrect >= 3 * this.intColumns) {
      if (this.gameFlag == 6 || this.gameFlag == 7 || this.gameFlag == 8) {
        this.blnRunEnd = true;
      } else {
        this.runEnd();
      }
    } else {
      if (this.gameFlag != 6) {
        this.board.enabled = true;
      }
    }
  }

  protected runAnimationBody() {
    UIUtil.hide(this.blocker);
    UIUtil.tween(
      this.blocker,
      'x',
      this.blockerX,
      150 * 2,
      undefined,
      this.resetAnimation.bind(this),
      undefined,
    );
    this.intLayer++;
  }

  protected useEarlyEnableStartButton() {
    return this.baseGameFlag == 5;
  }

  protected timeOutResetActivates() {
    return this.baseGameFlag == 5;
  }
}

export function VIFRulesMulDiv_prog(args: VifGameProgArgs) {
  new VIFRulesMulDivGame(args).run();
}
